import { Delete, Info } from "@mui/icons-material";
import { Box, IconButton, styled } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { drawersActions, useDispatchDrawers, useHasOpenDrawer } from "contexts/drawers";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { usePanelMode } from "contexts/panelMode";
import { sidePanelsActions, useDispatchSidePanels, useHasOpenPanels } from "contexts/sidePanels";
import { CORE_WINDOW_IDS } from "contexts/windows";
import { measureActions, selectMeasure } from "features/measure";
import { measureInteractionPositionsRef } from "features/measure/measureInteractionPositionsRef";
import {
    clippingOutlineLaserActions,
    selectLaserEditSessionStartSnapshot,
    selectOutlineLasers,
} from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLineCurrentIdx, selectPointLines } from "features/pointLine";
import { renderActions, selectPicker } from "features/render";
import { Picker } from "features/render/types";
import { undoRedoActions } from "features/undoRedo";

import {
    mobileMeasureActions,
    selectActivelyDrawing,
    selectDimensionSessionStart,
    selectPointLineMode,
    selectSelectedLaserId,
} from "./mobileMeasureSlice";

const OverlayButton = styled(IconButton)({
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    pointerEvents: "auto",
    backgroundColor: "#fff",
    color: "#222",
    "&:hover": { backgroundColor: "#f5f5f5" },
});

const TrashButton = styled(OverlayButton)({});

const InfoButton = styled(OverlayButton)({});

const ButtonGroup = styled(Box)({
    position: "absolute",
    display: "flex",
    gap: 6,
    transform: "translate(-50%, -100%)",
    marginTop: -10,
    pointerEvents: "auto",
});

interface OverlayPosition {
    x: number;
    y: number;
}

export function MeasurementOverlayButtons() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const dispatchDrawers = useDispatchDrawers();
    const dispatchSidePanels = useDispatchSidePanels();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const panelMode = usePanelMode();
    const picker = useAppSelector(selectPicker);
    const measure = useAppSelector(selectMeasure);
    const pointLines = useAppSelector(selectPointLines);
    const lasers = useAppSelector(selectOutlineLasers);
    const [, forceUpdate] = useState(0);

    const dimensionSessionStart = useAppSelector(selectDimensionSessionStart);
    const selectedLaserId = useAppSelector(selectSelectedLaserId);
    const laserSnapshot = useAppSelector(selectLaserEditSessionStartSnapshot);
    const pointLineMode = useAppSelector(selectPointLineMode);
    const pointLineCurrentIdx = useAppSelector(selectPointLineCurrentIdx);
    const activelyDrawing = useAppSelector(selectActivelyDrawing);
    const hasOpenDrawer = useHasOpenDrawer();
    const hasOpenPanels = useHasOpenPanels();

    useEffect(() => {
        if (!view) {
            return;
        }
        let rafId: number;
        const update = () => {
            forceUpdate((n) => n + 1);
            rafId = requestAnimationFrame(update);
        };
        rafId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(rafId);
    }, [view]);

    const getDimensionPositions = useCallback((): { pos: OverlayPosition; idx: number }[] => {
        const positions = measureInteractionPositionsRef.current;
        if (!positions) {
            return [];
        }
        return positions.remove
            .map((pos, idx) => (pos ? { pos: { x: pos[0], y: pos[1] }, idx } : null))
            .filter((item): item is { pos: OverlayPosition; idx: number } => item !== null);
    }, []);

    const getPointLinePositions = useCallback((): { pos: OverlayPosition; idx: number }[] => {
        if (!view) {
            return [];
        }
        return pointLines
            .map((line, idx) => {
                if (line.points.length === 0) {
                    return null;
                }
                const lastPoint = line.points[line.points.length - 1];
                const screenPos = view.measure?.draw.toMarkerPoints([lastPoint.position]);
                if (!screenPos?.[0]) {
                    return null;
                }
                return { pos: { x: screenPos[0][0], y: screenPos[0][1] }, idx };
            })
            .filter((item): item is { pos: OverlayPosition; idx: number } => item !== null);
    }, [view, pointLines]);

    const getLaserPositions = useCallback((): { pos: OverlayPosition; id: string }[] => {
        if (!view) {
            return [];
        }
        return lasers
            .map((laser) => {
                const screenPos = view.measure?.draw.toMarkerPoints([laser.laserPosition]);
                if (!screenPos?.[0]) {
                    return null;
                }
                return { pos: { x: screenPos[0][0], y: screenPos[0][1] }, id: laser.id };
            })
            .filter((item): item is { pos: OverlayPosition; id: string } => item !== null);
    }, [view, lasers]);

    const measurementPickers = [Picker.Dimension, Picker.PointLine, Picker.OutlineLaser];
    if (!measurementPickers.includes(picker)) {
        return null;
    }

    if (hasOpenDrawer || hasOpenPanels) {
        return null;
    }

    return (
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1090 }}>
            {/* Dimension overlays - show for the currently selected measurement */}
            {picker === Picker.Dimension &&
                getDimensionPositions()
                    .filter(({ idx }) => {
                        const set = measure.selectedEntities[idx];
                        return set && set.length > 0 && idx === measure.currentIndex;
                    })
                    .map(({ pos, idx }) => (
                        <ButtonGroup key={`dim-${idx}`} sx={{ left: pos.x, top: pos.y }}>
                            <InfoButton
                                size="small"
                                onClick={() => {
                                    dispatch(measureActions.selectMeasureSet(idx));
                                    if (panelMode === "sidePanel") {
                                        dispatchSidePanels(sidePanelsActions.openPanel(CORE_WINDOW_IDS.DIMENSION));
                                    } else {
                                        dispatchDrawers(drawersActions.openDrawer(CORE_WINDOW_IDS.DIMENSION));
                                    }
                                }}
                            >
                                <Info sx={{ fontSize: 20 }} />
                            </InfoButton>
                            <TrashButton
                                size="small"
                                onClick={() => {
                                    const nonEmptySets = measure.selectedEntities.filter((e) => e.length > 0).length;
                                    const isLastSessionMeasurement = nonEmptySets - 1 <= dimensionSessionStart;

                                    dispatch(measureActions.deleteMeasureSet(idx));
                                    dispatch(mobileMeasureActions.reset());

                                    if (isLastSessionMeasurement) {
                                        dispatch(renderActions.stopPicker(Picker.Dimension));
                                    }
                                }}
                            >
                                <Delete sx={{ fontSize: 20 }} />
                            </TrashButton>
                        </ButtonGroup>
                    ))}

            {/* Point-to-Point overlays - only show on finalized/selected measurements, not during active drawing */}
            {picker === Picker.PointLine &&
                !activelyDrawing &&
                (pointLineMode === "selected" || pointLineMode === "transparent") &&
                getPointLinePositions()
                    .filter(({ idx }) => idx === pointLineCurrentIdx && pointLines[idx]?.points.length >= 1)
                    .map(({ pos, idx }) => (
                        <ButtonGroup key={`ptl-${idx}`} sx={{ left: pos.x, top: pos.y }}>
                            <TrashButton
                                size="small"
                                onClick={() => {
                                    dispatch(pointLineActions.deletePointline(idx));
                                    dispatch(pointLineActions.startDetachedPointLine());
                                    dispatchLastPickSample(lastPickSampleActions.set(null));
                                    dispatch(mobileMeasureActions.reset());
                                }}
                            >
                                <Delete sx={{ fontSize: 20 }} />
                            </TrashButton>
                        </ButtonGroup>
                    ))}

            {/* Laser overlays - only show for the selected laser */}
            {picker === Picker.OutlineLaser &&
                getLaserPositions()
                    .filter(({ id }) => id === selectedLaserId)
                    .map(({ pos, id }) => (
                        <ButtonGroup key={`laser-${id}`} sx={{ left: pos.x, top: pos.y }}>
                            <TrashButton
                                size="small"
                                onClick={() => {
                                    // A "session laser" is one that was created during the active edit
                                    // session (i.e. not present in the snapshot taken at session start).
                                    // Count by ID rather than by length difference so that deleting
                                    // pre-existing (snapshotted) lasers during the session doesn't skew the count.
                                    const snapshotIds = new Set(laserSnapshot?.lasers.map((l) => l.id) ?? []);
                                    const isSessionLaser = !snapshotIds.has(id);
                                    const sessionLaserCount = laserSnapshot
                                        ? lasers.filter((l) => !snapshotIds.has(l.id)).length
                                        : lasers.length;
                                    const isLastSession = isSessionLaser && sessionLaserCount === 1;

                                    const removedLaser = lasers.find((l) => l.id === id);

                                    dispatch(clippingOutlineLaserActions.removeLaser(id));
                                    dispatch(mobileMeasureActions.setSelectedLaserId(null));

                                    if (isLastSession) {
                                        dispatch(mobileMeasureActions.reset());
                                        dispatch(renderActions.stopPicker(Picker.OutlineLaser));
                                    }

                                    if (removedLaser) {
                                        dispatch(
                                            undoRedoActions.push({
                                                description: "Remove laser measurement",
                                                undoCallback: () => {
                                                    dispatch(clippingOutlineLaserActions.addLaser(removedLaser));
                                                    dispatch(mobileMeasureActions.setSelectedLaserId(id));
                                                },
                                                redoCallback: () => {
                                                    dispatch(clippingOutlineLaserActions.removeLaser(id));
                                                    dispatch(mobileMeasureActions.setSelectedLaserId(null));
                                                },
                                            }),
                                        );
                                    }
                                }}
                            >
                                <Delete sx={{ fontSize: 20 }} />
                            </TrashButton>
                        </ButtonGroup>
                    ))}

            {/* Coordinates: delete handled by CoordinateLabelHUD inline button */}
        </Box>
    );
}
